import React, { useState } from 'react'
import { Box, Typography, Pill, Divider, Button, TextField } from 'components'
import { makeStyles, Chip, Popover } from '@material-ui/core'
import WorkOutlineIcon from '@material-ui/icons/WorkOutline'
import { useHistory } from 'react-router-dom'
import firebase from 'utils/firebase'
import { DEMO_REEL_ENABLED } from 'constants'

const useStyles = makeStyles(theme => ({
  sectionContainer: {
    backgroundColor: 'white',
    border: '1px solid #DDD',
    borderRadius: 10,
    marginBottom: theme.spacing(3)
  },
  skillChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.grey[100],
    '& .MuiChip-label': {
      fontSize: '0.85rem',
      fontWeight: 400,
      color: theme.palette.text.primary
    }
  },
  unionChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.light,
    '& .MuiChip-label': {
      fontSize: '0.85rem',
      fontWeight: 400,
      color: '#fff'
    }
  },
  unionContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  roleBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  roleText: {
    marginLeft: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  skillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}))

export const SkillPill = ({ role, skill, onClick }) => (
  <Pill
    key={skill}
    bgcolor={role.links && role.links[skill] ? '#000000' : 'white'}
    style={{ marginRight: 8, marginBottom: 8 }}
    textStyle={{ color: role.links && role.links[skill] ? '#ffffff' : '#111' }}
    onClick={onClick}
  >
    {skill}
  </Pill>
)

export default function FreelancerRolesSection({ freelancer, onEdit: propOnEdit }) {
  const classes = useStyles()
  const history = useHistory()
  const [demoPopoverOpen, setDemoPopoverOpen] = useState(false)
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const [demoUrl, setDemoUrl] = useState('')
  const ref = firebase.storage().ref(`/cvs/${freelancer.id}`)

  // If onEdit is provided as a prop, use it. Otherwise, redirect to our new route
  const onEdit = propOnEdit;

  const setUser = (data) =>
    firebase
      .firestore()
      .collection('users')
      .doc(freelancer.id)
      .set(data, { merge: true })

  const onDelete = () => {
    const shouldDelete = window.confirm(
      'Are you sure you want to remove your CV?',
    )
    if (!shouldDelete) return
    ref.delete().then(() => setUser({ cvUrl: null }))
  }

  const onUpload = (e) =>
    ref
      .put(e.target.files[0])
      .then((a) => a.ref.getDownloadURL().then((cvUrl) => setUser({ cvUrl })))

  const handleDemoReel = e => {
    if (demoPopoverOpen) {
      setUser({ demoUrl: demoUrl })
      setDemoPopoverOpen(false)
      return
    }
    if (!!freelancer.demoUrl) {
      setDemoUrl('')
      setUser({ demoUrl: null })
      return
    } else {
      setDemoUrl('')
      setDemoPopoverOpen(true)
      setPopoverAnchor(e.target)
    }
  }

  // Extract all unique skills from the freelancer
  const getAllSkills = () => {
    // First try to get skills from the flat skills array
    if (Array.isArray(freelancer.skills)) {
      return freelancer.skills;
    }

    // Return empty array if no skills found
    return [];
  };

  return (
    <Box className={classes.sectionContainer}>
      <Box flex={1} pt={3.5} px={3.5} pb={2}>
        <Box flex={1} flexVariant="row space-between" mb={3}>
          <Typography variant="h4" style={{ fontWeight: '600' }}>
            Expertise
          </Typography>

          <Box>
            {DEMO_REEL_ENABLED && (
              <Pill size="small" onClick={handleDemoReel} style={{ margin: '0px 8px' }}>
                {!!freelancer.demoUrl ? 'Remove Demo Reel URL' : 'Add Demo Reel URL'}
              </Pill>
            )}
            <Popover
              open={demoPopoverOpen}
              onClose={() => setDemoPopoverOpen(false)}
              anchorEl={popoverAnchor}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}>
              <Box padding={1}>
                <TextField onChange={e => setDemoUrl(e.target.value)} style={{ width: '100%' }} label="Enter Demo Reel URL" placeholder="https://www.youtube.com/watch?v=QH2-TGUlwu4" />
                <Button variant="contained" color="primary" size="small" onClick={handleDemoReel}>Save</Button>
                <Button color="secondary" size="small" onClick={() => setDemoPopoverOpen(false)}>Cancel</Button>
              </Box>
            </Popover>
            {!freelancer.cvUrl && (
              <input
                type="file"
                accept="application/pdf"
                id="file"
                style={{ display: 'none' }}
                onChange={onUpload}
              />
            )}
            {onEdit &&
              <>
                <Pill
                  component="label"
                  for="file"
                  size="small"
                  onClick={freelancer.cvUrl ? onDelete : () => { }}
                >
                  {freelancer.cvUrl ? 'Remove CV' : 'Upload CV'}
                </Pill>

                <Pill size="small" onClick={onEdit} style={{ margin: '0px 8px' }}>
                  Manage Expertise
                </Pill>

                <Pill size="small" onClick={onEdit}>
                  Add Links to Tags
                </Pill>
              </>
            }
            {!propOnEdit && freelancer.demoUrl && (
              <Button
                variant='contained'
                color="primary"
                component="a" target="_blank"
                href={freelancer.demoUrl}
                size="small"
                padding={14}
              >
                View Demo Reel
              </Button>
            )}
            {!propOnEdit && freelancer.cvUrl && (
              <Button
                variant="contained"
                color="primary"
                component="a"
                target="_blank"
                size="small"
                style={{ padding: 14 }}
                href={freelancer.cvUrl}
              >
                Download CV
              </Button>
            )}
          </Box>
        </Box>

        {/* Display unions if they exist */}
        {freelancer.unionStatus && freelancer.unionStatus !== 'Non-Union' && Array.isArray(freelancer.selectedUnions) && freelancer.selectedUnions.length > 0 && (
          <>
            <Box className={classes.unionContainer}>
              {freelancer.selectedUnions.map((union, index) => (
                <Chip
                  key={`${union.id}-${index}`}
                  label={`${union.name} (${union.memberType})`}
                  className={classes.unionChip}
                />
              ))}
            </Box>
            <Divider style={{ margin: '16px 0' }} />
          </>
        )}

        {/* Display roles with years experience */}
        {Array.isArray(freelancer.roles) ? (
          // Handle roles as an array (new format)
          freelancer.roles.map((role, index) => {
            const currentYear = new Date().getFullYear();
            const yearsOfExperience = role.yearStarted ? currentYear - role.yearStarted : role.experience || 0;

            return (
              <Box key={role.id || role.roleId || `role-${index}`} mb={2}>
                <Box className={classes.roleBox}>
                  <WorkOutlineIcon style={{ fill: '#696969', fontSize: 18 }} />
                  <Typography className={classes.roleText}>
                    {role.name || role.label} - {yearsOfExperience} years
                  </Typography>
                </Box>
                {/* Display skills for this role if available */}
                {Array.isArray(role.skills) && role.skills.length > 0 && (
                  <Box pl={4} mt={1} style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {role.skills.map((skill, skillIndex) => (
                      <SkillPill
                        key={`${skill}-${skillIndex}`}
                        role={role}
                        skill={skill}
                        onClick={
                          role.links && role.links[skill]
                            ? () => {
                              window.open(role.links[skill].startsWith('http') || role.links[skill].startsWith('https') ? (role.links[skill]) : `https://${role.links[skill]}`, '_blank')
                            }
                            : null
                        }
                      />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })
        ) : (
          // Handle roles as an object (old format)
          freelancer.roles && Object.values(freelancer.roles).map((role, index) => (
            <Box key={role.roleId || `role-${index}`} mb={2}>
              <Box className={classes.roleBox}>
                <WorkOutlineIcon style={{ fill: '#696969', fontSize: 18 }} />
                <Typography className={classes.roleText}>
                  {role.label} - {role.experience || 0} years
                </Typography>
              </Box>
              {/* Display skills for this role if available */}
              {Array.isArray(role.skills) && role.skills.length > 0 && (
                <Box pl={4} mt={1} style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {role.skills.map((skill, skillIndex) => (
                    <SkillPill
                      key={`${skill}-${skillIndex}`}
                      role={role}
                      skill={skill}
                      onClick={
                        role.links && role.links[skill]
                          ? () => {
                            window.open(role.links[skill].startsWith('http') || role.links[skill].startsWith('https') ? (role.links[skill]) : `https://${role.links[skill]}`, '_blank')
                          }
                          : null
                      }
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))
        )}

        {/* Display divider before skills */}
        <Divider style={{ margin: '16px 0' }} />

        {/* Display all skills as chips */}
        <Box className={classes.skillsContainer}>
          {getAllSkills().map((skill, index) => (
            <Chip
              key={`${skill}-${index}`}
              label={skill}
              className={classes.skillChip}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
